import {
  CanActivateChildFn,
  Route,
} from '@angular/router';
import { requireAnyPermission } from '../../../auth/permission-guard';
import { PAGE_PERMISSIONS } from '../../../auth/permissions';
import { DashboardMainContentService } from '../../../services/dashboard-main-content';
import { inject } from '@angular/core';

const useFullDashboardContentWidth: CanActivateChildFn = (childRoute) => {
  if (!childRoute.routeConfig?.path) {
    return true;
  }

  inject(DashboardMainContentService).setMaxWidth('100%');
  return true;
};

export const routes: Route[] = [
  {
    path: 'utilities',
    canActivateChild: [useFullDashboardContentWidth],
    children: [
      {
        path: 'program-safety-stock-orders',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.SAFETY_STOCK_EDIT)],
        data: { safetyStockUtility: 'ProgramSafetyStockOrders' },
        loadComponent: () =>
          import('./safety-stock/safety-stock').then(({ SafetyStockPage }) => SafetyStockPage),
      },
      {
        path: 'configure-program-safety-stock-materials',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.SAFETY_STOCK_EDIT)],
        data: { safetyStockUtility: 'ProgramSafetyStockMaterials' },
        loadComponent: () =>
          import('./safety-stock/safety-stock').then(({ SafetyStockPage }) => SafetyStockPage),
      },
      {
        path: 'lotto-rollout-safety-stock-orders',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.SAFETY_STOCK_EDIT)],
        data: { safetyStockUtility: 'LottoRolloutSafetyStockOrders' },
        loadComponent: () =>
          import('./safety-stock/safety-stock').then(({ SafetyStockPage }) => SafetyStockPage),
      },
      {
        path: '',
        loadComponent: () => import('./utilities').then(({ UtilitiesPage }) => UtilitiesPage),
      },
      {
        path: 'restore-orders-for-release',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.RELEASING)],
        loadComponent: () =>
          import('./restore-orders-for-release/restore-orders-for-release').then(
            ({ RestoreOrdersForReleasePage }) => RestoreOrdersForReleasePage,
          ),
      },
      {
        path: 'fixture-delivery-dates',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.TAKEOFF_EDIT)],
        loadComponent: () =>
          import('./fixture-delivery-schedules/fixture-delivery-schedules').then(
            ({ FixtureDeliverySchedulesPage }) => FixtureDeliverySchedulesPage,
          ),
      },
      {
        path: 'manage-milestone-schedules',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.MILESTONE_SCHEDULE_MANAGE)],
        loadComponent: () =>
          import('./manage-milestone-schedules/manage-milestone-schedules').then(
            ({ ManageMilestoneSchedulesPage }) => ManageMilestoneSchedulesPage,
          ),
      },
      {
        path: 'milestone-schedule-editor',
        loadComponent: () =>
          import('./milestone-schedule-editor/milestone-schedule-editor').then(
            ({ MilestoneScheduleEditorPage }) => MilestoneScheduleEditorPage,
          ),
      },
      {
        path: 'rules',
        loadComponent: () =>
          import('./special/take-off-rules/take-off-rules').then(
            ({ TakeOffRulesPage }) => TakeOffRulesPage,
          ),
      },
      {
        path: 'metal-rules',
        loadComponent: () =>
          import('./special/take-off-metal-rules/take-off-metal-rules').then(
            ({ TakeOffMetalRulesPage }) => TakeOffMetalRulesPage,
          ),
      },
      {
        path: 'planogram-fixture-notes',
        loadComponent: () =>
          import('./planogram-fixture-notes/planogram-fixture-notes').then(
            ({ PlanogramFixtureNotesPage }) => PlanogramFixtureNotesPage,
          ),
      },
      {
        path: 'wh-labor-forecast',
        loadComponent: () =>
          import('./warehouse-labor-forecast/warehouse-labor-forecast').then(
            ({ WarehouseLaborForecastPage }) => WarehouseLaborForecastPage,
          ),
      },
      {
        path: 'wh-labor-forecast/wh-labor-project-list',
        loadComponent: () =>
          import('./warehouse-labor-project-list/warehouse-labor-project-list').then(
            ({ WarehouseLaborProjectListPage }) => WarehouseLaborProjectListPage,
          ),
      },
      {
        path: 'wbs-override',
        loadComponent: () =>
          import('./wbs-override/wbs-override').then(({ WbsOverridePage }) => WbsOverridePage),
      },
      {
        path: 'inventory-expense-orders',
        loadComponent: () =>
          import('./inventory-expense-orders/inventory-expense-orders').then(
            ({ InventoryExpenseOrdersPage }) => InventoryExpenseOrdersPage,
          ),
      },
      {
        path: 'inventory-expense-orders/release',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.RELEASING_INVENTORY_EXPENSE)],
        loadComponent: () =>
          import('../releasing/inventory-expense-orders/inventory-expense-orders').then(
            ({ InventoryExpenseOrdersReleasingPage }) => InventoryExpenseOrdersReleasingPage,
          ),
      },
      {
        path: 'part-substitutions',
        loadComponent: () =>
          import('./part-substitutions/part-substitutions').then(
            ({ PartSubstitutionsPage }) => PartSubstitutionsPage,
          ),
      },
      {
        path: 'projects-in-scope',
        canActivate: [requireAnyPermission(PAGE_PERMISSIONS.PROJECTS_IN_SCOPE_EDIT)],
        loadComponent: () =>
          import('./projects-in-scope/projects-in-scope').then(
            ({ ProjectsInScopePage }) => ProjectsInScopePage,
          ),
      },
      {
        path: 'custom-release-logic',
        loadComponent: () =>
          import('./custom-release-logic/custom-release-logic').then(
            ({ CustomReleaseLogicPage }) => CustomReleaseLogicPage,
          ),
      },
      {
        path: 'packaging-groups',
        loadComponent: () =>
          import('./packaging-groups/packaging-groups').then(
            ({ PackagingGroupsPage }) => PackagingGroupsPage,
          ),
      },
      {
        path: 'inventory-comparison-part-equivalents',
        loadComponent: () =>
          import('./inventory-comparison-part-equivalents/inventory-comparison-part-equivalents').then(
            ({ InventoryComparisonPartEquivalentsPage }) => InventoryComparisonPartEquivalentsPage,
          ),
      },
      {
        path: 'lotto-sales-floor-spec-override',
        loadComponent: () =>
          import('./lotto-sales-floor-spec-override/lotto-sales-floor-spec-override').then(
            ({ LottoSalesFloorSpecOverridePage }) => LottoSalesFloorSpecOverridePage,
          ),
      },
      {
        path: 'takeoff-wave-status',
        loadComponent: () =>
          import('./takeoff-wave-status/takeoff-wave-status').then(
            ({ TakeoffWaveStatusPage }) => TakeoffWaveStatusPage,
          ),
      },
    ],
  },
];
